import type { ReactNode } from 'react';
import { useFocusMode } from '@/components/shell/focusMode';

export interface StickyActionBarProps {
  /** Usually one full-width primary Button. Two buttons sit side by side. */
  children: ReactNode;
  /** Short line above the button, e.g. "2 things left to fill in". Announced politely. */
  hint?: ReactNode;
  /**
   * Hide the phone tab bar (and the footer) while this bar is on screen. Default true: only one
   * bar ever sits at the bottom. Pass false to keep the tab bar; the action bar then sits right
   * above it and the page is padded for both.
   */
  hideTabBar?: boolean;
}

/**
 * The screen's one primary action, pinned to the bottom of the screen on long steps. Put it last
 * inside the page's `.rides-main`. Don't pin a disabled button: keep it enabled and, on tap,
 * scroll to the first missing answer with an inline error (or say what's left in `hint`).
 */
export function StickyActionBar({ children, hint, hideTabBar = true }: StickyActionBarProps) {
  useFocusMode(hideTabBar);
  return (
    <div className="rides-cta rides-actionbar">
      {hint && <p className="hint" aria-live="polite">{hint}</p>}
      <div className="row">{children}</div>
    </div>
  );
}
